import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import { partService } from '../services/partService';
import { labelTextColor } from './PartLabelChips';

const PRESET_COLORS = [
  '#64748b',
  '#2563eb',
  '#0891b2',
  '#059669',
  '#ca8a04',
  '#ea580c',
  '#dc2626',
  '#db2777',
  '#7c3aed',
  '#4f46e5'
];

/**
 * Multi-select labels with colored chips; New Label opens a name + color dialog.
 */
const PartLabelsField = ({
  value = [],
  onChange,
  options: optionsProp,
  disabled = false,
  size = 'small',
  label = 'Labels'
}) => {
  const [options, setOptions] = useState(() =>
    Array.isArray(optionsProp) ? optionsProp : []
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftColor, setDraftColor] = useState(PRESET_COLORS[1]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (Array.isArray(optionsProp)) {
      setOptions(optionsProp);
      return;
    }
    let cancelled = false;
    partService
      .getLabels()
      .then((res) => {
        if (!cancelled) setOptions(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [optionsProp]);

  const selected = useMemo(() => {
    const list = Array.isArray(value) ? value : [];
    return list.filter(Boolean);
  }, [value]);

  const mergedOptions = useMemo(() => {
    const byId = new Map();
    for (const o of options) {
      if (o?.id != null) byId.set(o.id, o);
    }
    for (const s of selected) {
      if (s?.id != null && !byId.has(s.id)) byId.set(s.id, s);
    }
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
    );
  }, [options, selected]);

  const openCreate = () => {
    setDraftName('');
    setDraftColor(PRESET_COLORS[1]);
    setError('');
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    const name = draftName.trim();
    if (!name) {
      setError('Name is required');
      return;
    }
    try {
      setCreating(true);
      setError('');
      const res = await partService.createLabel({ name, color: draftColor });
      const created = res?.data ?? res;
      setOptions((prev) => {
        const next = [...prev.filter((p) => p.id !== created.id), created];
        return next.sort((a, b) =>
          String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
        );
      });
      onChange?.([...selected.filter((s) => s.id !== created.id), created]);
      setDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create label');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Autocomplete
        multiple
        size={size}
        disabled={disabled}
        options={mergedOptions}
        value={selected}
        disableCloseOnSelect
        getOptionLabel={(o) => o?.name ?? ''}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        onChange={(_, next) => onChange?.(next)}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option.name}
                size="small"
                {...tagProps}
                sx={{
                  height: 22,
                  fontWeight: 600,
                  bgcolor: option.color || '#64748b',
                  color: labelTextColor(option.color),
                  '& .MuiChip-deleteIcon': { color: 'inherit', opacity: 0.85 }
                }}
              />
            );
          })
        }
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Chip
              label={option.name}
              size="small"
              sx={{
                height: 22,
                fontWeight: 600,
                bgcolor: option.color || '#64748b',
                color: labelTextColor(option.color)
              }}
            />
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={selected.length ? '' : 'Select labels…'}
            helperText="Optional — a part can have multiple labels"
          />
        )}
      />
      <Button size="small" onClick={openCreate} disabled={disabled} sx={{ mt: 0.75 }}>
        New Label…
      </Button>

      <Dialog open={dialogOpen} onClose={() => !creating && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Label</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Label name"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreate();
              }
            }}
            inputProps={{ maxLength: 50 }}
            sx={{ mt: 0.5, mb: 1.5 }}
            error={!!error}
            helperText={error || ' '}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
            Color
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {PRESET_COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => setDraftColor(c)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: c,
                  cursor: 'pointer',
                  outline: draftColor === c ? '2px solid' : '2px solid transparent',
                  outlineColor: draftColor === c ? 'primary.main' : 'transparent',
                  outlineOffset: 2
                }}
              />
            ))}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <Chip
              label={draftName.trim() || 'Preview'}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: draftColor,
                color: labelTextColor(draftColor)
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !draftName.trim()}>
            {creating ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartLabelsField;
